import React, { useEffect, useState } from 'react';
import { 
    Card, 
    CardContent, 
    FormControl, 
    Grid, 
    InputLabel, 
    MenuItem, 
    Select, 
    SelectChangeEvent 
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { TAnalyticParams } from '../../shared/types/analyticParameters.type';
import AvailableParams from './AvailableParams';
import DraggableParams from './DraggableParams';

type ParamsSectionProps = {
    loading: boolean;
    groupedParams: Partial<Record<string, TAnalyticParams[]>>;
    selectedParams: TAnalyticParams[];
    pinnedParams: TAnalyticParams[];
    onUpdateParam: (data: TAnalyticParams[]) => void;
    allParams: TAnalyticParams[];
    onParamCheck: (item: TAnalyticParams, checked: boolean) => void;
    isUpdating?: boolean;
    saveSettings: () => void;
    updatePin: (item: TAnalyticParams[]) => void;
};

function ParamsSection({ 
    loading, 
    groupedParams, 
    selectedParams, 
    onUpdateParam, 
    allParams, 
    onParamCheck, 
    isUpdating, 
    saveSettings, 
    pinnedParams, 
    updatePin 
}: ParamsSectionProps) {
    const [goto, setGoto] = useState<string>('');
    const { t } = useTranslation();

    // Effects
    useEffect(() => {
        if (!goto) return;
        
        const el = document.querySelector(`.${goto}`) as HTMLElement | null;
        
        if (el) {
            const parentContainer = el.parentElement;
            if (parentContainer) {
                parentContainer.scrollIntoView();
            }
            // Scroll to the target nested div with a slightly better positioning
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }, [goto]);

    const handleGotoChange = (e: SelectChangeEvent) => {
        setGoto(e.target.value);
    };

    return (
        <Card 
            elevation={2} 
            sx={{ 
                borderRadius: 2, 
                backgroundColor: 'background.paper',
                boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)' // Softer, more modern shadow
            }}
        >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                <Grid container spacing={4}>
                    {/* Top Section: Navigation Dropdown */}
                    <Grid item xs={12}>
                        <Grid container>
                            <Grid item xs={12} sm={8} md={4}>
                                <FormControl fullWidth size="small">
                                    <InputLabel id="goto-select-label">{t('settings:goTo')}</InputLabel>
                                    <Select
                                        labelId="goto-select-label"
                                        id="goto-select"
                                        label={t('settings:goTo')}
                                        value={goto}
                                        onChange={handleGotoChange}
                                        sx={{ borderRadius: 1.5 }}
                                    >
                                        {Object.keys(groupedParams).map((groupKey) => (
                                            <MenuItem value={groupKey} key={groupKey}>
                                                {t(`dataParameters:groups:${groupKey}`)}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* Bottom Section: Parameters */}
                    <Grid item md={6} sm={12} xs={12}>
                        <AvailableParams 
                            onParamCheck={onParamCheck} 
                            allParams={allParams} 
                            groupedParams={groupedParams} 
                            loading={loading} 
                            selectedParams={selectedParams} 
                        />
                    </Grid>

                    <Grid item md={6} sm={12} xs={12}>
                        <DraggableParams 
                            updatePin={updatePin} 
                            pinnedParams={pinnedParams} 
                            selectedParams={selectedParams} 
                            onUpdateParam={onUpdateParam} 
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
}

export default ParamsSection;